export type Colorway = {
  name: string;
  hex: string;
  img: string;
};

export type Audience = "men" | "women" | "unisex";

export type Product = {
  slug: string;
  name: string;
  category: string;
  categorySlug: string;
  audience: Audience;
  price: number;
  descriptor: string;
  img: string;
  gallery: string[];
  colorways: Colorway[];
  /** @deprecated use colorways */
  swatches: { name: string; hex: string }[];
  sizes?: string[];
  story: string;
  material: string;
  details: string[];
  tech: string[];
  scale?: number;
};

export const categories = [
  { slug: "outerwear", name: "Outerwear" },
  { slug: "tops", name: "Tops" },
  { slug: "bottoms", name: "Bottoms" },
  { slug: "footwear", name: "Footwear" },
  { slug: "packs", name: "Packs" },
  { slug: "accessories", name: "Accessories" },
];

// Original default packshots (kept as-is — these remain the default colorway per SKU)
const PACK = {
  lightweightShell: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_lightweight-shell.webp",
  insulatedJacket: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_insulated-jacket.webp",
  technicalTee: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_technical-tee.webp",
  baseLayer: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_base-layer.webp",
  alpinePant: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_alpine-softshell-pant.webp",
  hikingShort: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_hiking-short.webp",
  trailRunner: "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/77a8345f-12da-400b-b37a-e46bda19608b_compressed_09-trail-runner-lite.webp",
  hikingBoot: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_hiking-boot.webp",
  summitPack: "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/4fbb7d7f-b05f-4eb0-8512-d6a866059d96_compressed_11-summit-32l-pack.webp",
  travelPack: "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/d0725a9b-d4e1-4d73-840e-9c77334fc683_compressed_12-travel-pack.webp",
  fieldCap: "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/4cf23e9d-517b-4322-9887-b1c3c5373b25_compressed_13-field-cap-6-panel.webp",
  outdoorBottle: "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/5affa591-9f7f-4c6f-a855-8c86d9bd8823_compressed_14-outdoor-bottle.webp",
};

// Newly supplied additional colorway assets (compressed, <150KB each)
const CW = {
  lightweightShellSlate: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_lightweight-shell-slate.webp",
  insulatedJacketCharcoal: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_insulated-jacket-charcoal.webp",
  basecampFleeceCharcoal: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_basecamp-fleece-200-charcoal.webp",
  technicalTeeCharcoal: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_technical-tee-charcoal.webp",
  baseLayerCharcoal: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_base-layer-charcoal.webp",
  alpinePantMoss: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_alpine-softshell-pant-moss.webp",
  hikingShortSand: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_hiking-short-sand.webp",
  traverseShoeSandCharcoal: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_traverse-trail-shoe-sand-charcoal.webp",
  traverseShoeCharcoal: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_traverse-trail-shoe-charcoal.webp",
  trailRunnerLiteSandCharcoal: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_trail-runner-lite-sand-charcoal.webp",
  hikingBootSandCharcoal: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_hiking-boot-sand-charcoal.webp",
  summitPackSand: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_summit-32l-pack-sand.webp",
  summitPackClay: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_summit-32l-pack-clay.webp",
  travelPackCharcoal: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_travel-pack-charcoal.webp",
  fieldCapSand: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_field-cap-6-panel-sand.webp",
  fieldCapClay: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/127a9bc5-3273-4c62-9a05-d4f8ac1307c5_field-cap-6-panel-clay.webp",
};

// PDP supporting assets
const FLEECE_ONBODY = "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_basecamp-fleece-onbody.webp";
const TRAVERSE_DETAIL = "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/83987786-4265-4e67-a199-9d6d22191d2a_traverse-trail-shoe-detail.webp";

const FLEECE_PACK = "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/242bf67d-8139-40a0-ad42-dff9fb05278e_compressed_p03-fleece-packshot-web.webp";
const TRAVERSE_PACK = "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/66f38a2d-227c-4713-90b0-75a4d30d4f73_compressed_p05-trail-shoe-packshot-web.webp";
const FLEECE_MATERIAL = "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/771e4ac9-a59d-4db9-8eca-4a06edbe7dc6_compressed_home-tech-breathable-fabric-moss-web.webp";

// Palette (single source of truth for swatch hex values)
const HEX = {
  Charcoal: "#2A2A2A",
  Slate: "#4A5359",
  Moss: "#3D4A3A",
  Sand: "#C9B48F",
  Clay: "#B0623A",
  Black: "#1B1B1B",
  Terracotta: "#B5533A",
  // Two-tone swatches use the lighter tone
  SandCharcoal: "#C9B48F",
};

// Women's product assets (compressed WEBP, <150KB each)
const W = {
  lightweightShellSlate: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/bb4ae719-0522-4a82-8609-076377904506_women-lightweight-shell-slate.webp",
  lightweightShellTerracotta: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/bb4ae719-0522-4a82-8609-076377904506_women-lightweight-shell-terracotta.webp",
  fleeceClay: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/bb4ae719-0522-4a82-8609-076377904506_women-basecamp-fleece-200-clay.webp",
  fleeceSlate: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/bb4ae719-0522-4a82-8609-076377904506_women-basecamp-fleece-200-slate.webp",
  alpinePantMoss: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/bb4ae719-0522-4a82-8609-076377904506_women-alpine-softshell-pant-moss.webp",
  alpinePantCharcoal: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/bb4ae719-0522-4a82-8609-076377904506_women-alpine-softshell-pant-charcoal.webp",
  traverseShoeSlate: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/bb4ae719-0522-4a82-8609-076377904506_women-traverse-trail-shoe-slate.webp",
  traverseShoeClay: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/bb4ae719-0522-4a82-8609-076377904506_women-traverse-trail-shoe-clay.webp",
  traverseShoeMoss: "https://public.readdy.ai/ai/img_res/sandbox/9ba4bf4afd2936b8151e5d4e41d6be7f/bb4ae719-0522-4a82-8609-076377904506_women-traverse-trail-shoe-moss.webp",
};

const toSwatches = (cw: Colorway[]) => cw.map((c) => ({ name: c.name, hex: c.hex }));

export const products: Product[] = [
  // ————— Outerwear —————
  (() => {
    const cw: Colorway[] = [
      { name: "Charcoal", hex: HEX.Charcoal, img: PACK.lightweightShell },
      { name: "Slate", hex: HEX.Slate, img: CW.lightweightShellSlate },
    ];
    return {
      slug: "lightweight-shell",
      name: "Lightweight Shell",
      category: "Outerwear",
      categorySlug: "outerwear",
      audience: "men",
      price: 389,
      descriptor: "3-layer waterproof shell",
      img: PACK.lightweightShell,
      gallery: [PACK.lightweightShell],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["XS", "S", "M", "L", "XL"],
      story: "Built for the shoulder-season alpine, the Lightweight Shell moves quietly through weather most shells struggle with.",
      material: "3-layer recycled nylon with fully taped seams and a durable water-repellent finish.",
      details: ["Fully seam-taped 3L construction", "YKK AquaGuard front zip", "Helmet-compatible hood", "Underarm ventilation zips", "Weight: 420g / M"],
      tech: ["Weather Protection", "Engineered Construction"],
    };
  })(),
  (() => {
    const cw: Colorway[] = [
      { name: "Moss", hex: HEX.Moss, img: PACK.insulatedJacket },
      { name: "Charcoal", hex: HEX.Charcoal, img: CW.insulatedJacketCharcoal },
    ];
    return {
      slug: "insulated-jacket",
      name: "Insulated Jacket",
      category: "Outerwear",
      categorySlug: "outerwear",
      audience: "men",
      price: 329,
      descriptor: "Recycled synthetic insulation",
      img: PACK.insulatedJacket,
      gallery: [PACK.insulatedJacket],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["XS", "S", "M", "L", "XL"],
      story: "A quiet layer of warmth for the cold end of the shoulder season.",
      material: "60g recycled synthetic insulation with a windproof recycled nylon face.",
      details: ["60g mapped insulation", "Zippered hand pockets", "Internal storm flap", "Weight: 380g / M"],
      tech: ["Weather Protection"],
    };
  })(),

  // ————— Tops —————
  (() => {
    const cw: Colorway[] = [
      { name: "Moss", hex: HEX.Moss, img: FLEECE_PACK },
      { name: "Charcoal", hex: HEX.Charcoal, img: CW.basecampFleeceCharcoal },
    ];
    return {
      slug: "basecamp-fleece-200",
      name: "Basecamp Fleece 200",
      category: "Tops",
      categorySlug: "tops",
      audience: "men",
      price: 179,
      descriptor: "Recycled 200gsm grid fleece",
      img: FLEECE_PACK,
      gallery: [FLEECE_PACK, FLEECE_ONBODY, FLEECE_MATERIAL],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["XS", "S", "M", "L", "XL"],
      story: "A midlayer we reach for from October through May — quiet, warm, breathable.",
      material: "200gsm recycled polyester grid fleece with a soft brushed interior.",
      details: ["Half-zip pullover", "Zippered chest pocket", "Thumbholes at cuffs", "Weight: 340g / M"],
      tech: ["Breathable Performance"],
    };
  })(),
  (() => {
    const cw: Colorway[] = [
      { name: "Slate", hex: HEX.Slate, img: PACK.technicalTee },
      { name: "Charcoal", hex: HEX.Charcoal, img: CW.technicalTeeCharcoal },
    ];
    return {
      slug: "technical-tee",
      name: "Technical Tee",
      category: "Tops",
      categorySlug: "tops",
      audience: "men",
      price: 65,
      descriptor: "Merino-blend performance tee",
      img: PACK.technicalTee,
      gallery: [PACK.technicalTee],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["XS", "S", "M", "L", "XL"],
      story: "A weightless everyday layer for long days on the move.",
      material: "18.5μ merino wool blended with recycled Tencel for a soft, quick-drying hand.",
      details: ["Odor-resistant merino blend", "Flatlock seams", "Weight: 160g / M"],
      tech: ["Breathable Performance"],
    };
  })(),
  (() => {
    const cw: Colorway[] = [
      { name: "Clay", hex: HEX.Clay, img: PACK.baseLayer },
      { name: "Charcoal", hex: HEX.Charcoal, img: CW.baseLayerCharcoal },
    ];
    return {
      slug: "base-layer",
      name: "Base Layer",
      category: "Tops",
      categorySlug: "tops",
      audience: "men",
      price: 95,
      descriptor: "Long-sleeve merino base",
      img: PACK.baseLayer,
      gallery: [PACK.baseLayer],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["XS", "S", "M", "L", "XL"],
      story: "The first layer we put on and the last one we take off.",
      material: "180gsm merino wool with a fine flat-knit face.",
      details: ["Long sleeve crew neck", "Ergonomic seam placement", "Weight: 210g / M"],
      tech: ["Breathable Performance"],
    };
  })(),

  // ————— Bottoms —————
  (() => {
    const cw: Colorway[] = [
      { name: "Charcoal", hex: HEX.Charcoal, img: PACK.alpinePant },
      { name: "Moss", hex: HEX.Moss, img: CW.alpinePantMoss },
    ];
    return {
      slug: "alpine-softshell-pant",
      name: "Alpine Softshell Pant",
      category: "Bottoms",
      categorySlug: "bottoms",
      audience: "men",
      price: 229,
      descriptor: "Wind-resistant softshell",
      img: PACK.alpinePant,
      gallery: [PACK.alpinePant],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["28", "30", "32", "34", "36"],
      story: "Warm enough for wind on the ridge, breathable enough for the climb up.",
      material: "Recycled softshell with brushed backer and DWR finish.",
      details: ["Articulated knees", "Zippered hand pockets", "Adjustable waist", "Weight: 430g / M"],
      tech: ["Breathable Performance"],
    };
  })(),
  (() => {
    const cw: Colorway[] = [
      { name: "Slate", hex: HEX.Slate, img: PACK.hikingShort },
      { name: "Sand", hex: HEX.Sand, img: CW.hikingShortSand },
    ];
    return {
      slug: "hiking-short",
      name: "Hiking Short",
      category: "Bottoms",
      categorySlug: "bottoms",
      audience: "men",
      price: 89,
      descriptor: "Ripstop trail short",
      img: PACK.hikingShort,
      gallery: [PACK.hikingShort],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["28", "30", "32", "34", "36"],
      story: "Warm-day trail short that moves cleanly and dries fast.",
      material: "Recycled ripstop nylon with a slight stretch and DWR finish.",
      details: ["9\" inseam", "Zippered thigh pocket", "Weight: 210g / M"],
      tech: ["Breathable Performance"],
    };
  })(),

  // ————— Footwear —————
  (() => {
    const cw: Colorway[] = [
      { name: "Black", hex: HEX.Black, img: TRAVERSE_PACK },
      { name: "Sand / Charcoal", hex: HEX.SandCharcoal, img: CW.traverseShoeSandCharcoal },
      { name: "Charcoal", hex: HEX.Charcoal, img: CW.traverseShoeCharcoal },
    ];
    return {
      slug: "traverse-trail-shoe",
      name: "Traverse Trail Shoe",
      category: "Footwear",
      categorySlug: "footwear",
      audience: "men",
      price: 245,
      descriptor: "All-terrain trail shoe",
      img: TRAVERSE_PACK,
      gallery: [TRAVERSE_PACK, TRAVERSE_DETAIL],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["7", "8", "9", "10", "11", "12"],
      story: "Grippy on wet rock, quiet on long trail — our do-everything shoe for shoulder seasons.",
      material: "Recycled ripstop upper, Vibram Megagrip outsole, EVA midsole with rock plate.",
      details: ["Vibram Megagrip outsole", "Rock-plate protection", "6mm heel-toe drop", "Weight: 320g / size 9"],
      tech: ["Engineered Construction", "Weather Protection"],
    };
  })(),
  (() => {
    const cw: Colorway[] = [
      { name: "Clay", hex: HEX.Clay, img: PACK.trailRunner },
      { name: "Sand / Charcoal", hex: HEX.SandCharcoal, img: CW.trailRunnerLiteSandCharcoal },
    ];
    return {
      slug: "trail-runner-lite",
      name: "Trail Runner Lite",
      category: "Footwear",
      categorySlug: "footwear",
      audience: "men",
      price: 199,
      descriptor: "Lightweight fast-hike shoe",
      img: PACK.trailRunner,
      gallery: [PACK.trailRunner],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["7", "8", "9", "10", "11", "12"],
      story: "Fast, breathable, honest — for long trail days when weight matters.",
      material: "Engineered mesh upper with rubber outsole and cushioned EVA midsole.",
      details: ["Weight: 260g / size 9", "4mm heel-toe drop", "Breathable mesh upper"],
      tech: ["Breathable Performance"],
    };
  })(),
  (() => {
    const cw: Colorway[] = [
      { name: "Charcoal", hex: HEX.Charcoal, img: PACK.hikingBoot },
      { name: "Sand / Charcoal", hex: HEX.SandCharcoal, img: CW.hikingBootSandCharcoal },
    ];
    return {
      slug: "hiking-boot",
      name: "Hiking Boot",
      category: "Footwear",
      categorySlug: "footwear",
      audience: "men",
      price: 289,
      descriptor: "Waterproof mid hiking boot",
      img: PACK.hikingBoot,
      gallery: [PACK.hikingBoot],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["7", "8", "9", "10", "11", "12"],
      story: "Ankle support and waterproof reliability for loaded multi-day hikes.",
      material: "Full-grain leather upper with a waterproof membrane and Vibram outsole.",
      details: ["Vibram outsole", "Waterproof membrane", "Weight: 520g / size 9"],
      tech: ["Weather Protection", "Engineered Construction"],
    };
  })(),

  // ————— Packs —————
  (() => {
    const cw: Colorway[] = [
      { name: "Charcoal", hex: HEX.Charcoal, img: PACK.summitPack },
      { name: "Sand", hex: HEX.Sand, img: CW.summitPackSand },
      { name: "Clay", hex: HEX.Clay, img: CW.summitPackClay },
    ];
    return {
      slug: "summit-32l-pack",
      name: "Summit 32L Pack",
      category: "Packs",
      categorySlug: "packs",
      audience: "unisex",
      price: 289,
      descriptor: "32L alpine day pack",
      img: PACK.summitPack,
      gallery: [PACK.summitPack],
      colorways: cw,
      swatches: toSwatches(cw),
      story: "Big enough for a long alpine day, disciplined enough to disappear on your back.",
      material: "Recycled 420D ripstop nylon with reinforced base and roll-top closure.",
      details: ["32L capacity", "Roll-top closure", "Ice axe attachment", "Weight: 980g"],
      tech: ["Weather Protection", "Engineered Construction"],
    };
  })(),
  (() => {
    const cw: Colorway[] = [
      { name: "Slate", hex: HEX.Slate, img: PACK.travelPack },
      { name: "Charcoal", hex: HEX.Charcoal, img: CW.travelPackCharcoal },
    ];
    return {
      slug: "travel-pack",
      name: "Travel Pack",
      category: "Packs",
      categorySlug: "packs",
      audience: "unisex",
      price: 259,
      descriptor: "Carry-on ready 30L pack",
      img: PACK.travelPack,
      gallery: [PACK.travelPack],
      colorways: cw,
      swatches: toSwatches(cw),
      story: "For weekend flights, city crossings, and the trail in between.",
      material: "Recycled 420D ripstop nylon with a padded laptop sleeve.",
      details: ["30L capacity", "Padded 16\" laptop sleeve", "Lockable main zip", "Weight: 1.1kg"],
      tech: ["Engineered Construction"],
    };
  })(),

  // ————— Accessories —————
  (() => {
    const cw: Colorway[] = [
      { name: "Moss", hex: HEX.Moss, img: PACK.fieldCap },
      { name: "Sand", hex: HEX.Sand, img: CW.fieldCapSand },
      { name: "Clay", hex: HEX.Clay, img: CW.fieldCapClay },
    ];
    return {
      slug: "field-cap-6-panel",
      name: "Field Cap 6-Panel",
      category: "Accessories",
      categorySlug: "accessories",
      audience: "unisex",
      price: 45,
      descriptor: "Recycled cotton twill cap",
      img: PACK.fieldCap,
      gallery: [PACK.fieldCap],
      colorways: cw,
      swatches: toSwatches(cw),
      story: "The cap we always end up wearing.",
      material: "Recycled cotton twill with adjustable back strap.",
      details: ["6-panel construction", "Adjustable strap", "Embroidered logo"],
      tech: [],
    };
  })(),
  (() => {
    const cw: Colorway[] = [
      { name: "Charcoal", hex: HEX.Charcoal, img: PACK.outdoorBottle },
    ];
    return {
      slug: "outdoor-bottle",
      name: "Outdoor Bottle",
      category: "Accessories",
      categorySlug: "accessories",
      audience: "unisex",
      price: 38,
      descriptor: "Insulated 750ml bottle",
      img: PACK.outdoorBottle,
      gallery: [PACK.outdoorBottle],
      colorways: cw,
      swatches: toSwatches(cw),
      story: "Cold water for twelve hours. Hot for six. Quiet on the pack strap.",
      material: "18/8 stainless steel with a powder-coated exterior.",
      details: ["750ml capacity", "Vacuum insulated", "Leak-proof cap"],
      tech: [],
    };
  })(),
  // ————— Women's ————— (new SKUs)
  (() => {
    const cw: Colorway[] = [
      { name: "Slate", hex: HEX.Slate, img: W.lightweightShellSlate },
      { name: "Terracotta", hex: HEX.Terracotta, img: W.lightweightShellTerracotta },
    ];
    return {
      slug: "women-lightweight-shell",
      name: "Women Lightweight Shell",
      category: "Outerwear",
      categorySlug: "outerwear",
      audience: "women" as Audience,
      price: 389,
      descriptor: "3-layer waterproof shell",
      img: W.lightweightShellSlate,
      gallery: [W.lightweightShellSlate, W.lightweightShellTerracotta],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["XS", "S", "M", "L", "XL"],
      story: "A women's shell tuned for shoulder-season weather — quiet, weatherproof, and cut to move.",
      material: "3-layer recycled nylon with fully taped seams and a durable water-repellent finish.",
      details: ["Fully seam-taped 3L construction", "YKK AquaGuard front zip", "Helmet-compatible hood", "Underarm ventilation zips", "Weight: 380g / M"],
      tech: ["Weather Protection", "Engineered Construction"],
    };
  })(),
  (() => {
    const cw: Colorway[] = [
      { name: "Clay", hex: HEX.Clay, img: W.fleeceClay },
      { name: "Slate", hex: HEX.Slate, img: W.fleeceSlate },
    ];
    return {
      slug: "women-basecamp-fleece-200",
      name: "Women Basecamp Fleece 200",
      category: "Outerwear",
      categorySlug: "outerwear",
      audience: "women" as Audience,
      price: 179,
      descriptor: "Recycled 200gsm grid fleece",
      img: W.fleeceClay,
      gallery: [W.fleeceClay, W.fleeceSlate],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["XS", "S", "M", "L", "XL"],
      story: "A women's-cut midlayer — quiet, warm, breathable, and built for daily use.",
      material: "200gsm recycled polyester grid fleece with a soft brushed interior.",
      details: ["Half-zip pullover", "Zippered chest pocket", "Thumbholes at cuffs", "Weight: 310g / M"],
      tech: ["Breathable Performance"],
    };
  })(),
  (() => {
    const cw: Colorway[] = [
      { name: "Moss", hex: HEX.Moss, img: W.alpinePantMoss },
      { name: "Charcoal", hex: HEX.Charcoal, img: W.alpinePantCharcoal },
    ];
    return {
      slug: "women-alpine-softshell-pant",
      name: "Women Alpine Softshell Pant",
      category: "Bottoms",
      categorySlug: "bottoms",
      audience: "women" as Audience,
      price: 229,
      descriptor: "Wind-resistant softshell",
      img: W.alpinePantMoss,
      gallery: [W.alpinePantMoss, W.alpinePantCharcoal],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["00", "0", "2", "4", "6", "8", "10", "12"],
      story: "Warm enough for wind on the ridge, breathable enough for the climb up — in a women's fit.",
      material: "Recycled softshell with brushed backer and DWR finish.",
      details: ["Articulated knees", "Zippered hand pockets", "Adjustable waist", "Weight: 400g / M"],
      tech: ["Breathable Performance"],
    };
  })(),
  (() => {
    const cw: Colorway[] = [
      { name: "Slate", hex: HEX.Slate, img: W.traverseShoeSlate },
      { name: "Clay", hex: HEX.Clay, img: W.traverseShoeClay },
      { name: "Moss", hex: HEX.Moss, img: W.traverseShoeMoss },
    ];
    return {
      slug: "women-traverse-trail-shoe",
      name: "Women Traverse Trail Shoe",
      category: "Footwear",
      categorySlug: "footwear",
      audience: "women" as Audience,
      price: 245,
      descriptor: "All-terrain trail shoe",
      img: W.traverseShoeSlate,
      gallery: [W.traverseShoeSlate, W.traverseShoeClay, W.traverseShoeMoss],
      colorways: cw,
      swatches: toSwatches(cw),
      sizes: ["5", "6", "7", "8", "9", "10", "11"],
      story: "Grippy on wet rock, quiet on long trail — a women's-last do-everything shoe for shoulder seasons.",
      material: "Recycled ripstop upper, Vibram Megagrip outsole, EVA midsole with rock plate.",
      details: ["Vibram Megagrip outsole", "Rock-plate protection", "6mm heel-toe drop", "Weight: 280g / size 7"],
      tech: ["Engineered Construction", "Weather Protection"],
    };
  })(),
];

export function getProduct(slug: string) {
  return products.find((p) => p.slug === slug);
}

export function getProductsByCategory(slug: string) {
  return products.filter((p) => p.categorySlug === slug);
}